/** @file Page to configure payments and processing */
import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PageWrapper from '../../common/PageWrapper';
import {
  MerchantExternalCredential,
  PaymentGatewayType,
  findPaymentGatewayType,
  normalizeGateway,
  BillingCredentialData,
  BankCredentialData,
  BankSystemOfOrigin,
  DEFAULT_BANK_SYSTEM_OF_ORIGIN,
  UpdateDefaultCredentialsBody,
} from '../../store/modules/merchant/types';
import { testId } from './util';
import { useMerchant } from '../../contexts/MerchantContext';
import {
  useGetConfigsQuery,
  useGetCredentialsQuery,
  useUpdateDefaultCredentialsMutation,
  useCreateCredentialMutation,
  useGetCallbacksQuery,
} from '../../store/modules/merchant/api';
import { useConfigUpsert, useCallbackUpsert } from '../../hooks';
import { orderConfigsForPartialPaymentMutualExclusion } from '../../util/partialPaymentConfig';
import { OnSaveChanges, PaymentsAndProcessingForm } from './components/PaymentsAndProcessingForm';
import { toast } from '../../util/toast';
import Logger from '../../util/logger';
/**
 * Main page for merchants to edit their payment and processing settings
 */
export const PaymentsAndProcessing = () => {
  const {
    merchantId,
    merchantName,
    isLoading: infoLoading,
    canUpdateMerchantSettings,
  } = useMerchant();

  const {
    data: credentialData,
    isLoading: isLoadingCredentials,
    fulfilledTimeStamp: credentialFulfilledTimeStamp,
  } = useGetCredentialsQuery(
    { merchantId, type: ['payment', 'ecommerce', 'billing', 'bank'] },
    { skip: !merchantId },
  );
  const paymentCredentials = (credentialData?.payment?.credentials ??
    []) as MerchantExternalCredential<PaymentGatewayType>[];
  const ecommerceCredentials = credentialData?.ecommerce?.credentials ?? [];
  const billingCredentials = (credentialData?.billing?.credentials ??
    []) as MerchantExternalCredential<'Worldpay_Billing', BillingCredentialData>[];
  const bankCredentials = (credentialData?.bank?.credentials ?? []) as MerchantExternalCredential<
    BankSystemOfOrigin,
    BankCredentialData
  >[];

  const {
    data: configs = [],
    isLoading: isLoadingConfigs,
    fulfilledTimeStamp: configFulfilledTimeStamp,
  } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });

  const {
    data: callbacks = [],
    isLoading: isLoadingCallbacks,
    fulfilledTimeStamp: callbackFulfilledTimeStamp,
  } = useGetCallbacksQuery(merchantId, {
    skip: !merchantId,
  });

  const isCustom = ecommerceCredentials.some(
    (credential) => credential?.systemOfOrigin === 'custom',
  );
  const { onConfigUpsertBatch, isLoading: isUpsertingConfig } = useConfigUpsert(merchantId);
  const { onCallbackUpsertBatch, isLoading: isUpsertingCallback } = useCallbackUpsert(merchantId);
  const [onCredentialCreate, { isLoading: isUpsertingCredential }] = useCreateCredentialMutation();
  const [onUpdateDefaultCredentials, { isLoading: isUpdatingDefaultCredentials }] =
    useUpdateDefaultCredentialsMutation();
  const isSaving =
    isUpsertingConfig ||
    isUpsertingCredential ||
    isUpdatingDefaultCredentials ||
    isUpsertingCallback;

  const existingBankCredential = bankCredentials.find(
    (c) => c.systemOfOrigin === DEFAULT_BANK_SYSTEM_OF_ORIGIN,
  );

  const onSave = async (changes: OnSaveChanges) => {
    try {
      // Handle configs
      if (changes.configs && changes.configs.length > 0) {
        const orderedConfigs = orderConfigsForPartialPaymentMutualExclusion(changes.configs);
        await onConfigUpsertBatch(orderedConfigs);
      }

      // Handle callbacks
      if (changes.callbacks && changes.callbacks.length > 0) {
        await onCallbackUpsertBatch(changes.callbacks);
      }

      // Handle credentials
      let updatedCredentials: MerchantExternalCredential[] = [];
      if (changes.credentials && changes.credentials.length > 0) {
        updatedCredentials = await Promise.all(
          changes.credentials.map((credential) =>
            onCredentialCreate({
              merchantId,
              credentialType: credential.credentialType,
              systemOfOrigin: credential.systemOfOrigin,
              data: credential.data,
            }).unwrap(),
          ),
        );
      }

      const newDefaultCredentials: UpdateDefaultCredentialsBody = {};

      // Update default gateway if needed
      if (changes.selectedGateway) {
        const selectedGateway = changes.selectedGateway;
        const credential =
          updatedCredentials.find(
            (c) => normalizeGateway(c.systemOfOrigin) === normalizeGateway(selectedGateway),
          ) ??
          paymentCredentials.find(
            (c) => normalizeGateway(c.systemOfOrigin) === normalizeGateway(selectedGateway),
          );
        if (credential) {
          newDefaultCredentials.payment = credential.credentialId;
        }
      }

      // Disable or enable bank payments by updating the default credential
      if (changes.bankEnabled === false) {
        newDefaultCredentials.bank = null;
      } else if (changes.bankEnabled === true) {
        const createdBankCredential = updatedCredentials.find((c) => c.credentialType === 'bank');
        if (createdBankCredential?.credentialId) {
          newDefaultCredentials.bank = createdBankCredential.credentialId;
        } else {
          if (!existingBankCredential) {
            // This should never happen
            throw new Error('Enabling bank payments failed. Bank credential not found');
          }
          newDefaultCredentials.bank = existingBankCredential.credentialId;
        }
      }

      if (Object.keys(newDefaultCredentials).length > 0) {
        await onUpdateDefaultCredentials({
          merchantId,
          externalCredentials: newDefaultCredentials,
        }).unwrap();
      }

      toast.success('Changes saved successfully');
    } catch (err) {
      Logger.error('Error saving payments and processing settings', err);
      toast.error(
        'An error occurred while saving these changes. Please try again later or contact Skipify Support',
      );
    }
  };

  const { fisBillingFields } = useFlags();
  const hasModifyAccess = canUpdateMerchantSettings();

  const isLoading = infoLoading || isLoadingConfigs || isLoadingCredentials || isLoadingCallbacks;

  // Generate a key for the PaymentsAndProcessingForm to ensure proper remounting
  const generateFormKey = () => {
    const merchantIdForKey = paymentCredentials[0]?.merchantId || configs[0]?.merchantId;

    return [
      merchantIdForKey,
      credentialFulfilledTimeStamp,
      configFulfilledTimeStamp,
      callbackFulfilledTimeStamp,
    ].join('-');
  };

  return (
    <PageWrapper
      showParentHierarchyBanner
      data-testid={testId}
      title="Payments & Processing"
      description="Use this page to configure your Payment Capture and Payment Gateway options."
      isLoading={isLoading}
    >
      <PaymentsAndProcessingForm
        key={generateFormKey()}
        billingCredential={billingCredentials?.find(
          (c) => c.credentialId === credentialData?.billing?.default,
        )}
        bankEnabled={Boolean(credentialData?.bank?.default)}
        bankCredential={
          bankCredentials.find((c) => c.credentialId === credentialData?.bank?.default) ??
          existingBankCredential
        }
        configs={configs}
        callbacks={callbacks}
        isCustomMerchant={isCustom}
        paymentCredentials={paymentCredentials}
        readOnly={!hasModifyAccess}
        selectedPaymentCredential={findPaymentGatewayType(
          paymentCredentials.find((c) => c.credentialId === credentialData?.payment?.default)
            ?.systemOfOrigin,
        )}
        usingBillingFields={fisBillingFields}
        merchantName={merchantName}
        onSave={onSave}
        isSaving={isSaving}
      />
    </PageWrapper>
  );
};
